import { CollectionInfoModel } from "@phdsoft/common";
import { DomainEntity } from "../DomainEntity";
import { Region } from "../Framework/Region";
import { WorkableElement } from "../SectionElements/WorkableElement";
import * as THREE from '@phdsoft/three';

export type CampaignStatus = 'Outlined' | 'NONE' | 'Executed';
// export type CampaignType = 'StructureCampaign' | 'InspectionCampaign' | 'RepairCampaign' | 'ResourceCampaign' | 'DocumentCampaign' | 'SensorCampaign';
export const campaignTypes = [
  'InspectionCampaign',
  'RepairCampaign',
  'SensorCampaign',
  'CriticalAreaCampaign',
  'StructureCampaign',
  'ResourceCampaign',
  'DocumentCampaign',
  'ProtectionElementsCampaign',
  'LightStructureCampaign',
  'TemporalElementsCampaign',
  'StructuralConnectionCampaign',
] as const
export type CampaignType = typeof campaignTypes[number];
export class Collection extends DomainEntity {

    public group: THREE.Group;
    public cssGroup: THREE.Group;

    //#region Dates
    private _Date: Date;
    public get Date(): Date {
        return this._Date;
    }
    public set Date(v: Date) {
        this._Date = v;
    }

    private _InsertDate: Date;
    public get InsertDate(): Date {
        return this._InsertDate;
    }
    public set InsertDate(v: Date) {
        this._InsertDate = v;
    }

    //#endregion


    private _assetId: string;
    public get assetId(): string {
        return this._assetId;
    }
    public set assetId(v: string) {
        this._assetId = v;
    }


    private _CollectionInfoModel: CollectionInfoModel;
    public get CollectionInfoModel(): CollectionInfoModel {
        return this._CollectionInfoModel;
    }
    public set CollectionInfoModel(v: CollectionInfoModel) {
        this._CollectionInfoModel = v;
    }

    public isGroupRecursive: boolean;

    private _CampaignInfo: CollectionInfoModel;
    public get CampaignInfo(): CollectionInfoModel {
        return this._CampaignInfo;
    }
    public set CampaignInfo(v: CollectionInfoModel) {
        this._CampaignInfo = v;
    }

    private _IsVisible: boolean;
    public get IsVisible(): boolean {
        return this._IsVisible;
    }
    public set IsVisible(v: boolean) {
        this._IsVisible = v;
    }

    private comments: string;
    public get Comments(): string {
        return this.comments;
    }
    public set Comments(value: string) {
        this.comments = value;
    }

    private _CampaignStatus: CampaignStatus;
    public get CampaignStatus(): CampaignStatus {
        return this._CampaignStatus;
    }
    public set CampaignStatus(v: CampaignStatus) {
        this._CampaignStatus = v;
    }
    private _Type: CampaignType;
    public get Type(): CampaignType {
        return this._Type;
    }
    public set Type(v: CampaignType) {
        this._Type = v;
    }

    private _regionsWorkableElements: Array<Region> = new Array<Region>();
    public get regionsWorkableElements(): Array<Region> {
        return this._regionsWorkableElements;
    }
    public set regionsWorkableElements(v: Array<Region>) {
        this._regionsWorkableElements = v;
    }

    public get ElementsCount(): number {
        return this._regionsWorkableElements.map(x => x.ElementsCount).reduce((a, b) => a + b);;
    }

    public get Elements(): Array<WorkableElement> {
        return this.SurfaceElements.concat(this.LinearElements)
    }

    public get SurfaceElements(): Array<WorkableElement> {
        return this.regionsWorkableElements.flatMap(x => x.SurfaceElements);
    }

    public get LinearElements(): Array<WorkableElement> {
        return this.regionsWorkableElements.flatMap(x => x.LinearElements);
    }

    public get VisibleRegionsGroups(): Array<THREE.Group> {
        return this._regionsWorkableElements.filter(x => x.visible === true).map(x => x.group);
    }

    public get VisibleRegions(): Array<Region> {
        return this._regionsWorkableElements.filter(x => x.visible === true);
    }

    constructor(date: Date) {
        super();
        this.group = new THREE.Group();
        this.cssGroup = new THREE.Group();
        this._Date = date;
    }

    public RegionById(regionId: string): Region {
        return this._regionsWorkableElements.find(x => x.Guid === regionId);
    }

    public setSectionsVisibility(regions: { [id: string]: { visible: boolean, sections: { [id: string]: boolean } } }): void {
        for (const region of this._regionsWorkableElements) {
            if (regions[region.Guid] !== undefined && regions[region.Guid] !== null) {
                region.visible = regions[region.Guid].visible;
            }
        }
    }

    public setRegionsVisibility(regions: { [id: string]: { visible?: boolean, sections?: { [id: string]: boolean } } }): void {
        for (const region of this._regionsWorkableElements) {
            if (regions[region.Guid] !== undefined && regions[region.Guid] !== null) {
                region.visible = regions[region.Guid].visible;
                for (const section of region.sections) {
                  const sectionVisible = regions[region.Guid].sections !== null && regions[region.Guid].sections !== undefined &&
                      regions[region.Guid].sections[section.Guid] !== undefined && regions[region.Guid].sections[section.Guid] !== null
                      ? regions[region.Guid].sections[section.Guid]
                      : region.visible;
                  section.group.visible = sectionVisible;
                  section.surfaceGroup.visible = sectionVisible;
                  if (!sectionVisible) {
                      section.linearGroup.visible = false;
                  }
              }
          }
      }
    }

    public setVisibilityForAll(v: boolean): void {
        for (const region of this._regionsWorkableElements) {
            region.visible = v;
            region.sectionsVisibility = v;
            for (const section of region.sections) {
                section.visible = v;
                section.clearBorders();
                section.linearGroup.visible = false;
            }
        }
    }

    //#region Load methods

    public LoadGltfFile() {

    }

    //#endregion

    public getElementById(regionId: string, sectionId: string, id: string): WorkableElement {
        const region = this._regionsWorkableElements.find(x => x.Guid === regionId);
        if (region) return region.getElementById(sectionId, id);
        return null;
    }

    public getElementByIdOnly(regionId: string, id: string): WorkableElement {
        const region = this._regionsWorkableElements.find(x => x.Guid === regionId);
        if (region) return region.getElementByIdOnly(id);
        return null;
    }

    public deleteElementByIdOnly(regionId: string, id: string): void {
        const region = this._regionsWorkableElements.find(x => x.Guid === regionId);
        if (region) region.deleteElementByIdOnly(id);
    }

    public deleteElementLinearElementByIdOnly(regionId: string, id: string): void {
        const region = this._regionsWorkableElements.find(x => x.Guid === regionId);
        if (region) region.deleteLinearElementByIdOnly(id);
    }

    public addSurfaceElement(element: WorkableElement): void {
        const region = this.regionsWorkableElements.find(x => x.Guid === element.regionId);
        if (region) region.addSurfaceElement(element);
        else {
            const newRegion: Region = new Region(element.regionId, element.regionName);
            this.addRegion(newRegion);
            newRegion.addSurfaceElement(element);
        }
        element.Campaign = this;
    }

    public addLinearElement(element: WorkableElement, target: WorkableElement = null): void {
        const region = this.regionsWorkableElements.find(x => x.Guid === element.regionId);
        if (region) region.addLinearElement(element, target);
        else {
            const newRegion: Region = new Region(element.regionId, element.regionName);
            this.addRegion(newRegion);
            newRegion.addLinearElement(element, target);
        }
        element.Campaign = this;
    }

    public addSurfaceCSSElement(element: WorkableElement): void {
        const region = this.regionsWorkableElements.find(x => x.Guid === element.regionId);
        if (region) region.addSurfaceCSSElement(element);

    }

    public addRegion(region: Region): void {
        this.group.add(region.group);
        this.regionsWorkableElements.push(region);
    }
}
